import { defineAsyncComponent, toRaw } from 'vue';

import lodash from 'lodash';
import i18n from '../../../../../i18n';

import { required, requiredWhen, validateError } from '../../../../../utils/validate';

import {
    createFieldCheckbox,
    createFieldInput, createFieldInputNumber,
    createFieldSelect, createFieldTemplateBuilder
} from '../../../../../components/input/template-builder/template-builder.logic';

import store from '../../../../../store';
import {
    actionPrefix, callActive, callSave, callSearch,
    isLoading,
    routerPrefix,
    setIsLoading,
    state
} from './topic-unit-list.logic';
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickArchive,
    onClickCreate, onClickDelete,
    onClickEdit,
    onClickView, putField
} from '../../../../../utils/form';
import { useForm as createUseForm } from '@ant-design-vue/use';
import { setAppPageHeader } from '../../../../../components/layout/app-layout/app-layout.logic';
import { createAppPageHeaderTop } from '../../../../../utils/page';
import { setIsLoading as setIsLoadingApp } from '../../../../../app.logic';
import {
    callSearchLookup,
    getGroupLookupByKey,
    getLabTypeKeyByRoleMenuAttributes,
    strConvertToUnderScore
} from '../lab-share.logic';
import { has } from '../../../../../utils/menu-context';
import {
    createButtonActive, createButtonArchive,
    createButtonCreate, createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from '../../../../../utils/button';
import { Col, Row } from 'ant-design-vue';

const CardBorder = defineAsyncComponent(() => import('../../../../../components/card/CardBorder'));
const TemplateBuilder = defineAsyncComponent(() => import('../../../../../components/input/template-builder/TemplateBuilder'));

export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.value : i18n.global.t('button.create');
};
export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ['**'],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
            children: []
        };
    }
};
export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch((error) => {
            validateError({ error });
            throw error;
        });
};
export const onClickSave = async ({ data }) => {
    setIsLoadingApp(true);
    return data.useForm
        .validate()
        .then(() => {
            // data.useForm.modelRef.value = data.useForm.modelRef.key;
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then((record) => onClickView({ record, routerPrefix }));
        })
        .catch((error) => validateError({ error }));
};

export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, {
    id,
    config: { params: { isHardDelete: false } }
});

export const createRules = ({ data }) => {
    return {
        seq: [],
        key: [required],
        value: [required],
        refParent: [requiredWhen(() => data.isChild)],
        [`attribute.base`]: [requiredWhen(() => data.isChild)]
    };
};
export const createForm = () => {
    return {
        isSave: true,
        seq: null,
        key: null,
        value: null,
        refLabType: null,
        attribute: {},
    };
};
export const createFieldForTopicUnit = ({ props, data, key, isLoading }) => {
    data.section.fields = createField({ props, data, key, isLoading });
    data.changeLogFields.push(...data.section.fields);
    return [
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => <CardBorder title={i18n.global.t('model.lookup.modelGroup.LAB_UNIT')}>
                <TemplateBuilder
                    rowProp={data.rowProp}
                    useForm={data.useForm}
                    fields={data.section.fields}
                    skeleton={isLoading.value}
                ></TemplateBuilder>
            </CardBorder>
        }
    ];
};
export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { id, isActive } = data.useForm.modelRef;
    if (id && has({ menuContext, key: 'canCreate' })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: 'm-1' }));
    }
    if (!viewOnly && id && has({ menuContext, key: 'canView' })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: 'm-1' }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: 'canEdit' })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: 'm-1' }));
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs }), classStyle: 'm-1' }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: 'canActive' })) {
        col2.push(createButtonActive({
            onClick: () => onClickActive({
                record: { id },
                callActive,
                setIsLoading: setIsLoadingApp,
                onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
            }), classStyle: 'm-1'
        }));
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canArchive' })) {
        col2.push(createButtonArchive({
            onClick: () => onClickArchive({
                record: { id },
                callArchive,
                setIsLoading: setIsLoadingApp,
                onClickView: () => onClickView({ record: { id }, routerPrefix })
            }), classStyle: 'm-1'
        }));
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canDelete' })) {
        col2.push(createButtonDelete({
            onClick: () => onClickDelete({
                record: { id },
                callDelete,
                setIsLoading: setIsLoadingApp,
                state,
                routerPrefix
            }), classStyle: 'm-1'
        }));
    }
    return <Row type='flex'>
        <Col>{col1}</Col>
        <Col class='ml-auto'>{col2}</Col>
    </Row>;
};


export const createField = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : '';
    return [
        createFieldSelect({
            visible: () => data.labAttributes.length > 1,
            key: `${prefix}refLabType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.lookup.modelGroup.LAB_TYPE'),
            inputProp: () => ({
                disabled: viewOnly || getLabTypeKeyByRoleMenuAttributes({ props }).length < 2,
                value: model.refLabType,
                'onUpdate:value': value => {
                    model.group = strConvertToUnderScore({
                        input: {
                            data: value.key,
                            transform: { suffix: '_UNIT' }
                        }
                    });
                    return model.refLabType = value;
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_TYPE',
                    value: searchQuery
                }),
                optLabel: 'value'
            })
        }),
        createFieldInput({
            key: `${prefix}key`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.labTopicUnit.key'),
            inputProp: () => ({
                value: model.key,
                'onUpdate:value': value => {
                    model.key = value;
                },
                disabled: viewOnly
            })
        }),
        createFieldInput({
            key: `${prefix}value`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.labTopicUnit.value'),
            inputProp: () => ({
                value: model.value,
                'onUpdate:value': value => {
                    model.value = value;
                },
                disabled: viewOnly
            })
        }),
        createFieldCheckbox({
            visible: () => model.refLabType.key == 'labPhysic',
            key: `${prefix}isChild`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.labTopicUnit.isChild'),
            inputProp: () => ({
                checked: data.isChild,
                value: data.isChild,
                'onUpdate:checked': value => {
                    data.isChild = value;
                    if (!value) {
                        model.attribute = null;
                        model.refParent = null;
                    } else {
                        model.attribute = {}
                    }
                },
                disabled: viewOnly
            })
        }),
        createFieldInputNumber({
            visible: () => data.isChild && model.refLabType.key == 'labPhysic',
            key: `${prefix}attribute.base`,
            colProp: () => ({ xs: 24, md: { offset: 5, span: 4 } }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.labTopicUnit.base'),
            inputProp: () => ({
                value: model.attribute?.base,
                'onUpdate:value': value => model.attribute.base = value,
                disabled: viewOnly,
                maxPrecision: 3,
                allowPrecision: true,
                comma: false
            })
        }),
        createFieldSelect({
            visible: () => data.isChild && model.refLabType.key == 'labPhysic',
            key: `${prefix}refParent`,
            colProp: () => ({ xs: 24, md: 6}),
            formItemProp: () => ({ wrapperCol: { xs: 24, md: 6 } }),
            inputProp: () => ({
                disabled: viewOnly || !model.key,
                value: model.refParent,
                'onUpdate:value': value => model.refParent = value,
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: lodash.toUpper(lodash.snakeCase(model.refLabType.key)) + '_UNIT',
                    key: searchQuery
                }).then(arr => arr.filter(it => it.key != model.key && it.refParent == null)),
                optLabel: 'value'
            })
        }),
        createFieldTemplateBuilder({
            visible: () => model.refLabType.key == 'labPhysic',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('views.labTopicUnit.isReferLabTopicKey'),
            inputProp: () => ({
                useForm: useForm,
                fields: putField({
                    data, fields: [
                        createFieldCheckbox({
                            key:  `${prefix}attribute.isNW`,
                            colProp: () => ({ xs: 24, md: 16 }),
                            formItemProp: () => ({ wrapperCol: { xs: 24, md: 18 } }),
                            inputProp: () => ({ text: () => i18n.global.t('views.labTopicUnit.isNW'), checked: modelRef.attribute.isNW, 'onUpdate:checked': checked => modelRef.attribute['isNW'] = checked, disabled: viewOnly })
                        }),
                        createFieldCheckbox({
                            key:  `${prefix}attribute.isDW`,
                            colProp: () => ({ xs: 24, md: 16 }),
                            formItemProp: () => ({ wrapperCol: { xs: 24, md: 18 } }),
                            inputProp: () => ({ text: () => i18n.global.t('views.labTopicUnit.isDW'), checked: modelRef.attribute.isDW, 'onUpdate:checked': checked => modelRef.attribute['isDW'] = checked, disabled: viewOnly })
                        }),
                        createFieldCheckbox({
                            key:  `${prefix}attribute.isNV`,
                            colProp: () => ({ xs: 24, md: 16 }),
                            formItemProp: () => ({ wrapperCol: { xs: 24, md: 18 } }),
                            inputProp: () => ({ text: () => i18n.global.t('views.labTopicUnit.isNV'), checked: modelRef.attribute.isNV, 'onUpdate:checked': checked => modelRef.attribute['isNV'] = checked, disabled: viewOnly })
                        }),
                    ]
                })
            })
        }),
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs, isLoading });
    data.rules = createRules({ props, data, refs, isLoading });
    const labTypeRoleMenuAttributes = getLabTypeKeyByRoleMenuAttributes({ props });
    if (labTypeRoleMenuAttributes.length > 0) {
        if (labTypeRoleMenuAttributes.length == 1) {
            callSearchLookup({
                criteria: {
                    select: ['*'],
                    page: 0,
                    size: 20,
                    where: [
                        { name: 'group', operator: '=', type: 'String', value: 'LAB_TYPE' },
                        { name: 'key', operator: '=', type: 'String', value: labTypeRoleMenuAttributes[0] }
                    ]
                }
            }).then(res => {
                data.form.refLabType = res.data[0];
            });
            data.form.group = strConvertToUnderScore({
                input: {
                    data: labTypeRoleMenuAttributes[0],
                    transform: { suffix: '_UNIT' }
                }
            });
        }
        data.rules.refLabType = [required];
    }
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createFieldForTopicUnit({ props, data, refs, isLoading });
    loadForm({ props, data, refs })
        .then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({
                            props,
                            data,
                            refs,
                            state,
                            routerPrefix,
                            onClickView,
                            onClickEdit
                        }),
                        default: () => createAppPageHeaderDefault({ props, data, refs })
                    }
                });
            }
        });
};


export const loadForm = ({ props, data, refs }) => {
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(async (page) => {
            if (page.data.length == 1) {
                const labTypeStr = lodash.replace(lodash.camelCase(page.data[0].group), 'Unit', '');
                const labType = await getGroupLookupByKey({ searchQuery: labTypeStr, group: 'LAB_TYPE' });
                modelRef.refLabType = labType[0];
            }
            data.isChild = page.data[0]?.refParent ? true : false;
            return initUseForm({ props, data, refs, page, routerPrefix });
        })
        .finally(() => setIsLoading(false));
};


